import { useState, useEffect, useCallback, Fragment } from 'react'
import { AdminAlert } from '../AdminShared'
import { Skeleton } from '../components/Skeleton'
import { adminGetReferralOverview, adminGetReferralsPerUser, adminGetUserReferrals } from '../../../services/admin'

function StatCard({ label, value, icon }) {
  return (
    <div className="border-4 border-on-background bg-surface p-5 flex items-center gap-4"
      style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
      <div className="w-11 h-11 flex items-center justify-center border-2 border-on-background bg-primary-container shrink-0">
        <span className="material-symbols-outlined text-[22px] text-on-primary-container">{icon}</span>
      </div>
      <div className="flex flex-col">
        <p className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">{label}</p>
        <p className="font-mono font-extrabold text-[22px] leading-tight">{value}</p>
      </div>
    </div>
  )
}

export function ReferralAnalytics() {
  const [overview, setOverview] = useState(null)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [expanded, setExpanded] = useState(null) // profileId currently expanded
  const [detail, setDetail] = useState({}) // profileId -> referrals[] | 'loading' | Error
  const [search, setSearch] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [ov, perUser] = await Promise.all([
        adminGetReferralOverview(),
        adminGetReferralsPerUser(),
      ])
      setOverview(ov)
      setRows(perUser)
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const toggleExpand = async (profileId) => {
    if (expanded === profileId) { setExpanded(null); return }
    setExpanded(profileId)
    if (detail[profileId] && detail[profileId] !== 'error') return
    setDetail(prev => ({ ...prev, [profileId]: 'loading' }))
    try {
      const referrals = await adminGetUserReferrals(profileId)
      setDetail(prev => ({ ...prev, [profileId]: referrals }))
    } catch (err) {
      setDetail(prev => ({ ...prev, [profileId]: 'error' }))
    }
  }

  const filteredRows = rows.filter(r => {
    const q = search.trim().toLowerCase()
    if (!q) return true
    return (r.username ?? '').toLowerCase().includes(q) || (r.fullName ?? '').toLowerCase().includes(q)
  })

  if (loading) {
    return (
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 w-full rounded" />)}
        </div>
        <Skeleton className="h-64 w-full rounded" />
      </div>
    )
  }

  if (error) {
    return <AdminAlert type="error" message={error.message ?? 'Failed to load referral analytics'} />
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Referrals" value={overview?.totalReferrals ?? 0} icon="group_add" />
        <StatCard label="Active Referrals" value={overview?.activeReferrals ?? 0} icon="how_to_reg" />
        <StatCard label="Conversion Rate" value={`${overview?.conversionRate ?? 0}%`} icon="trending_up" />
        <StatCard label="Total Rewards Paid" value={overview?.totalReferralRewards ?? 0} icon="redeem" />
      </div>

      <div className="relative w-full">
        <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-[18px] text-on-surface-variant">search</span>
        <input
          type="text" placeholder="Filter by username or name…"
          value={search} onChange={e => setSearch(e.target.value)}
          className="w-full bg-surface border-4 border-on-background pl-11 pr-4 py-3 font-body-md focus:ring-0 focus:outline-none focus:border-primary-container transition-all"
        />
      </div>

      {filteredRows.length === 0 ? (
        <div className="border-4 border-dashed border-on-background/25 bg-surface-container-low p-10 text-center">
          <p className="text-on-surface-variant font-bold text-[12px] uppercase tracking-widest">No referrers found</p>
        </div>
      ) : (
        <div className="border-4 border-on-background bg-surface overflow-x-auto"
          style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
          <table className="w-full text-left">
            <thead>
              <tr className="border-b-4 border-on-background bg-surface-container-low">
                <th className="px-4 py-3 font-bold text-[10px] uppercase tracking-widest">Referrer</th>
                <th className="px-4 py-3 font-bold text-[10px] uppercase tracking-widest text-right">Total</th>
                <th className="px-4 py-3 font-bold text-[10px] uppercase tracking-widest text-right">Active</th>
                <th className="px-4 py-3 font-bold text-[10px] uppercase tracking-widest text-right">Conv. %</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map(r => {
                const total = Number(r.totalReferrals ?? 0)
                const active = Number(r.activeReferrals ?? 0)
                const conv = total === 0 ? 0 : ((active / total) * 100).toFixed(1)
                const isOpen = expanded === r.profileId
                const d = detail[r.profileId]
                return (
                  <Fragment key={r.profileId}>
                    <tr className="border-b-2 border-on-background/15 hover:bg-surface-container-low/50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-bold text-[13px]">{r.fullName || '—'}</p>
                        <p className="text-[12px] text-on-surface-variant">{r.username ? `@${r.username}` : '—'}</p>
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-bold">{total}</td>
                      <td className="px-4 py-3 text-right font-mono font-bold">{active}</td>
                      <td className="px-4 py-3 text-right font-mono font-bold">{conv}%</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => toggleExpand(r.profileId)}
                          className="px-3 py-1.5 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-primary-container hover:text-on-primary-container transition-colors rounded">
                          {isOpen ? 'Hide' : 'View'}
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-b-2 border-on-background/15 bg-surface-container-low/40">
                        <td colSpan={5} className="px-4 py-4">
                          {d === 'loading' ? (
                            <Skeleton className="h-12 w-full rounded" />
                          ) : d === 'error' ? (
                            <AdminAlert type="error" message="Failed to load this user's referrals" />
                          ) : !d || d.length === 0 ? (
                            <p className="text-[12px] text-on-surface-variant font-bold uppercase tracking-widest">No referrals recorded</p>
                          ) : (
                            <div className="flex flex-col gap-2">
                              {d.map(ref => (
                                <div key={ref.id} className="flex items-center justify-between gap-3 border-2 border-on-background/20 bg-surface px-3 py-2 rounded flex-wrap">
                                  <div className="flex flex-col">
                                    <p className="font-bold text-[12px]">{ref.referred?.fullName || '—'}</p>
                                    <p className="text-[11px] text-on-surface-variant">{ref.referred?.username ? `@${ref.referred.username}` : '—'}</p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className={`px-2 py-0.5 border-2 text-[10px] font-bold uppercase tracking-widest rounded ${ref.activityRewarded ? 'border-primary-container bg-primary-container/20 text-on-primary-container' : 'border-on-surface-variant/40 text-on-surface-variant'}`}>
                                      {ref.activityRewarded ? 'Active' : 'Pending'}
                                    </span>
                                    <span className="text-[11px] text-on-surface-variant font-mono">
                                      {ref.createdAt ? new Date(ref.createdAt).toLocaleDateString() : '—'}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}